import React, { useMemo, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
} from "@mui/material";
import { LoadingPlanSteps } from "../../CreateLoadingPlanPage";
import { useLoadingPlan } from "../../../../contexts/LoadingPlanContext";
import { TableComponent, TableColumn } from "../../../../components/ui/TableComponent/TableComponent";
import "./Step3.scss";
import partsService, { Part } from "../../../../services/api/partsService";

interface GroupedPart extends Part {
  groupedQty: number;
  originalIndices: number[];
}

interface Step3Props {
  setCurrentStep: (step: LoadingPlanSteps) => void;
  isGenerating: boolean;
  isGenerated: boolean;
}

const Step3: React.FC<Step3Props> = (props: Step3Props) => {
  const { isGenerating } = props;
  const { currentLoadingPlan } = useLoadingPlan();

  const [includedParts, setIncludedParts] = useState<Part[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [weights, setWeights] = useState<{ total: number; loaded: number }>({ total: 0, loaded: 0 });

  // Fetch included parts from backend
  useEffect(() => {
    if (!currentLoadingPlan?.id) return;

    const fetchIncludedParts = async () => {
      setLoading(true);
      try {
        const response = await partsService.fetchParts({
          loadingPlanId: currentLoadingPlan.id,
          limit: rowsPerPage,
          offset: page * rowsPerPage,
          isIncluded: true
        });
        setIncludedParts(response.parts);
        setTotal(response.total);
      } catch (error) {
        console.error('Failed to fetch included parts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchIncludedParts();
  }, [currentLoadingPlan?.id, page, rowsPerPage]);

  // Fetch weights
  useEffect(() => {
    if (!currentLoadingPlan?.id) return;

    const fetchWeights = async () => {
      try {
        const weightsData = await partsService.fetchWeights(currentLoadingPlan.id);
        setWeights(weightsData);
      } catch (error) {
        console.error('Failed to fetch weights:', error);
      }
    };

    fetchWeights();
  }, [currentLoadingPlan?.id, includedParts]);

  // Define table columns
  const columns: TableColumn<Part>[] = useMemo(() => [
    {
      id: 'name',
      label: 'Name',
      className: 'table-cell-name',
      render: (row) => row.name,
    },
    {
      id: 'mark',
      label: 'Mark',
      className: 'table-cell-mark',
      render: (row) => row.mark,
    },
    {
      id: 'sequence',
      label: 'Sequence',
      className: 'table-cell-sequence',
      render: (row) => row.sequenceNumber,
    },
    {
      id: 'type',
      label: 'Type',
      className: 'table-cell-type',
      render: (row) => row.type,
    },
    {
      id: 'subType',
      label: 'Sub Type',
      className: 'table-cell-sub-type',
      render: (row) => row.subType || '-',
    },
    {
      id: 'qty',
      label: 'Qty',
      className: 'table-cell-qty',
      render: (row) => row.qty,
    },
    {
      id: 'weight',
      label: 'Total Weight, lbs',
      className: 'table-cell-weight',
      render: (row) => (row.weight != null ? row.weight.toFixed(2) : '-'),
    },
  ], []);

  return (
    <div className="step3">
      <Paper className="form-card">
        <Typography variant="subtitle1" fontWeight={600} mb={1}>
          Step 3: Generate Loading Plan
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Review settings and start nesting algorithm
        </Typography>

        {/* Summary Boxes */}
        <Box className="summary-boxes">
          <Box className="summary-box">
            <Typography variant="subtitle2" color="text.secondary">
              Parts
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {total || 0}
            </Typography>
          </Box>
          <Box className="summary-box">
            <Typography variant="subtitle2" color="text.secondary">
              Trailer
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {currentLoadingPlan.trailerLength ? `${currentLoadingPlan.trailerLength.toFixed(2)} ft` : '-'}
            </Typography>
          </Box>
          <Box className="summary-box">
            <Typography variant="subtitle2" color="text.secondary">
              Max Weight Capacity
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {currentLoadingPlan.trailerMaxWeight ? `${currentLoadingPlan.trailerMaxWeight.toFixed(2)} lbs` : '-'}
            </Typography>
          </Box>
          <Box className="summary-box">
            <Typography variant="subtitle2" color="text.secondary">
              Total Weight
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {weights.loaded > 0
                ? `${weights.loaded.toFixed(2)} lbs`
                : '-'}
            </Typography>
          </Box>
        </Box>
        {isGenerating && (
          <Box className="generating-container">
            <CircularProgress />
            <Typography className="generating-text">Optimizing layout...</Typography>
            <Typography>Running nesting algorithm with business rules</Typography>
          </Box>
        )}
      </Paper>

      {/* Included Table Section */}
      <Paper className="form-card included-section">
        <TableComponent
          data={includedParts}
          columns={columns}
          title="Included"
          loading={loading}
          emptyMessage="No parts included. Please go back to Step 1 to add parts."
          paginated={true}
          page={page}
          rowsPerPage={rowsPerPage}
          total={total}
          onPageChange={(_event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          disablePaper={true}
          size="small"
        />
      </Paper>
    </div>
  );
};

export { Step3 };
